<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'
import { useSession } from '@/stores/session.js'
import { SPOT_SIZES, usePrivacy } from '@/stores/privacy.js'

/**
 * Privacy mode at work (D-260), over the whole application:
 *
 * - discreet: the values marked private are blurred (CSS); one shows while pointed at or
 *   focused, and on a touch screen for three seconds after it is held;
 * - full: a blurring pane over everything, with windows where it may be read — the row, card or
 *   field under the pointer (or finger), the field being typed in, the application's own
 *   navigation;
 * - spot: the same pane with one tiny window, a few pixels around the pointer or finger (its size
 *   set by the person) — and while one types, at the text cursor;
 * - discreet + spot: the pane only over the sensitive values (what discreet masks), with the same
 *   tiny window — the rest of the page as readable as ever;
 * - the screen covered: Escape twice, the button, or — as the person set — on leaving the
 *   window or after minutes untouched; back with a click, a tap or a key.
 *
 * Ctrl+Shift+P starts from the strictest: off → spot → full → discreet + spot → discreet → off. Alt with the mouse
 * wheel makes the spot bigger or smaller.
 */
const privacy = usePrivacy()
const session = useSession()
// The modes drawn with the pane, and those with the spot.
const paned = () => ['full', 'spot', 'discreet_spot'].includes(privacy.mode)
const spotty = () => ['spot', 'discreet_spot'].includes(privacy.mode)

// ---------------------------------------------------------------- keys

let lastEscape = 0
// Typing: the spot goes to the text cursor (until the pointer moves).
let typing = false
function onKey(event) {
  if (spotty() && editable(document.activeElement) && !['Shift', 'Control', 'Alt', 'Meta'].includes(event.key)) {
    typing = true
    redraw()
  }
  if (privacy.covered) {
    event.preventDefault()
    privacy.uncover()
    return
  }
  if (event.ctrlKey && event.shiftKey && (event.key === 'P' || event.key === 'p')) {
    event.preventDefault()
    privacy.cycle()
    return
  }
  if (event.key === 'Escape' && privacy.on) {
    const now = Date.now()
    if (now - lastEscape < 450) privacy.cover()
    lastEscape = now
  }
}

// ---------------------------------------------------------------- covered on leaving, after idle minutes

const coverOnLeave = computed(() => privacy.on && !!session.me?.settings?.privacy_cover_on_leave)
const idleMinutes = computed(() => (privacy.on ? Number(session.me?.settings?.privacy_cover_idle) || 0 : 0))
let lastActive = Date.now()
const active = () => (lastActive = Date.now())
const leave = () => coverOnLeave.value && privacy.cover()
const onVisibility = () => document.visibilityState === 'hidden' && leave()
let idleTimer = null

// ---------------------------------------------------------------- discreet: held on a touch screen

let holdTimer = null
function onTouchStart(event) {
  active()
  if (paned()) return lens(event.touches[0])
  if (privacy.mode !== 'discreet') return
  const target = event.target.closest?.('[data-private], [data-private-field]')
  if (!target) return
  clearTimeout(holdTimer)
  holdTimer = setTimeout(() => {
    target.classList.add('pv-peek')
    setTimeout(() => target.classList.remove('pv-peek'), 3000)
  }, 450)
}
const onTouchEnd = () => clearTimeout(holdTimer)

// ---------------------------------------------------------------- full: the windows in the pane

const mask = ref('none')
// Where the spot is (for the tests and whoever looks).
const spotAt = ref('')
const pointer = { x: -1, y: -1 }
const UNITS = 'tr, li, [role=row], [role=option], [data-lens], .group, .card, section, label, td, p, dd, h1, h2, h3'
const PUBLIC = '[data-privacy-public]'
let frame = 0

/** The part of an element on the screen (a table's row runs on past it, scrolled sideways). */
function visible(el) {
  const r = el.getBoundingClientRect()
  let { left, top, right, bottom } = r
  for (let box = el.parentElement; box && box !== document.body; box = box.parentElement) {
    const style = getComputedStyle(box)
    if (style.overflowX === 'visible' && style.overflowY === 'visible') continue
    const b = box.getBoundingClientRect()
    left = Math.max(left, b.left)
    top = Math.max(top, b.top)
    right = Math.min(right, b.right)
    bottom = Math.min(bottom, b.bottom)
  }
  return { left: Math.max(0, left), top: Math.max(0, top), right: Math.min(window.innerWidth, right), bottom: Math.min(window.innerHeight, bottom) }
}
const small = (r) => r.bottom - r.top <= window.innerHeight * 0.4 && r.right - r.left <= window.innerWidth * 0.95

/** The readable window at a point: the nearest row, card or field there, not too big. */
function unitAt(x, y) {
  if (x < 0) return null
  let el = document.elementFromPoint(x, y)
  if (!el || el.closest('.pv-pane')) return null
  for (let node = el.closest(UNITS); node; node = node.parentElement?.closest(UNITS)) {
    if (small(visible(node))) el = node
    else break
    if (node.matches('tr, li, [role=row], [data-lens], .group')) break
  }
  const rect = visible(el)
  if (!small(rect)) {
    // Nothing small enough: a band around the point.
    return { left: Math.max(0, x - 200), top: Math.max(0, y - 48), right: x + 200, bottom: y + 48 }
  }
  return rect
}
function focusUnit() {
  const el = document.activeElement
  if (!el || el === document.body) return null
  const unit = el.closest('.group, [data-lens], tr, li, .dt-field, [role=dialog] form') || el
  const rect = visible(unit)
  return rect.bottom - rect.top > window.innerHeight * 0.5 ? visible(el) : rect
}
// ---------------------------------------------------------------- spot: a few words around a point

const editable = (el) => !!el && (el.isContentEditable || (el.tagName === 'TEXTAREA') || (el.tagName === 'INPUT' && !['checkbox', 'radio', 'button', 'submit', 'range', 'color', 'file'].includes(el.type)))
const MIRRORED = ['boxSizing', 'width', 'height', 'overflowX', 'overflowY', 'borderTopWidth', 'borderRightWidth', 'borderBottomWidth', 'borderLeftWidth', 'paddingTop', 'paddingRight', 'paddingBottom', 'paddingLeft',
  'fontStyle', 'fontVariant', 'fontWeight', 'fontStretch', 'fontSize', 'lineHeight', 'fontFamily', 'textAlign', 'textTransform', 'textIndent', 'letterSpacing', 'wordSpacing', 'tabSize']

/** Where the text cursor is on the screen. */
function caret() {
  const el = document.activeElement
  if (!editable(el)) return null
  if (el.isContentEditable) {
    const selection = window.getSelection()
    if (!selection?.rangeCount) return null
    const range = selection.getRangeAt(0).cloneRange()
    range.collapse(false)
    const rect = range.getClientRects()[0] || range.startContainer.parentElement?.getBoundingClientRect()
    return rect ? { x: rect.left, y: rect.top + rect.height / 2 } : null
  }
  // A field's text drawn again, hidden, as far as the cursor: where it ends is where the cursor is.
  const style = getComputedStyle(el)
  const mirror = document.createElement('div')
  for (const p of MIRRORED) mirror.style[p] = style[p]
  Object.assign(mirror.style, { position: 'fixed', visibility: 'hidden', top: '0', left: '0', whiteSpace: el.tagName === 'TEXTAREA' ? 'pre-wrap' : 'pre', overflowWrap: 'break-word' })
  mirror.textContent = el.value.slice(0, el.selectionEnd ?? el.value.length)
  const mark = document.createElement('span')
  mark.textContent = '\u200b'
  mirror.appendChild(mark)
  document.body.appendChild(mirror)
  const box = el.getBoundingClientRect()
  const x = box.left + mark.offsetLeft - el.scrollLeft
  const y = box.top + mark.offsetTop - el.scrollTop + mark.offsetHeight / 2
  mirror.remove()
  return { x: Math.min(x, box.right - 4), y }
}

const EDGE = 48

// Alt + the wheel: the spot's size, where the pointer is.
function onWheel(event) {
  if (!spotty() || !event.altKey) return
  event.preventDefault()
  privacy.resizeSpot(event.deltaY < 0 ? 1 : -1)
}
watch(() => privacy.spotSize, () => redraw())

/**
 * The pane's mask: opaque (blurred) everywhere but the windows. The windows are added up, never
 * cut one out of another, so the spot over the menu or the top bar leaves them as readable as
 * they are.
 */
// What discreet masks (see index.css), found on the page: for discreet + spot.
const SENSITIVE = [
  '[data-private]',
  '[data-private-field] > :not(.field-label)',
  ':is(.doc-canvas, .dv-viewer) :is(p, li, td, th, h1, h2, h3, h4, h5, h6, .dv-cell)',
  ':is([data-privacy-cells], .table-crm:not([data-testid=data-table])) :is(td, dd, li, p):not(:has(td, dd, li, p))',
].join(', ')

/** The sensitive values on the screen, as rectangles. */
function sensitiveRects() {
  const w = window.innerWidth
  const h = window.innerHeight
  // Each scrolling box's visible part, once per drawing.
  const clips = new Map()
  const clipOf = (box) => {
    if (!box || box === document.body || box === document.documentElement) return { left: 0, top: 0, right: w, bottom: h }
    if (clips.has(box)) return clips.get(box)
    const outer = clipOf(box.parentElement)
    const style = getComputedStyle(box)
    let clip = outer
    if (style.overflowX !== 'visible' || style.overflowY !== 'visible') {
      const b = box.getBoundingClientRect()
      clip = { left: Math.max(outer.left, b.left), top: Math.max(outer.top, b.top), right: Math.min(outer.right, b.right), bottom: Math.min(outer.bottom, b.bottom) }
    }
    clips.set(box, clip)
    return clip
  }
  const rects = []
  for (const el of document.querySelectorAll(SENSITIVE)) {
    const r = el.getBoundingClientRect()
    if (r.bottom < 0 || r.top > h || r.right < 0 || r.left > w || !r.width || !r.height) continue
    const c = clipOf(el.parentElement)
    const rect = { left: Math.max(r.left, c.left), top: Math.max(r.top, c.top), right: Math.min(r.right, c.right), bottom: Math.min(r.bottom, c.bottom) }
    if (rect.right > rect.left && rect.bottom > rect.top) rects.push(rect)
  }
  return rects
}

function draw() {
  frame = 0
  if (!paned() || privacy.covered) return
  const w = window.innerWidth
  const h = window.innerHeight
  let spot = ''
  if (spotty()) {
    const at = (typing && caret()) || (pointer.x >= 0 ? pointer : null)
    if (at) {
      spotAt.value = `${Math.round(at.x)},${Math.round(at.y)}`
      const [rx, ry] = SPOT_SIZES[privacy.spotSize]
      spot = `<ellipse cx="${Math.round(at.x)}" cy="${Math.round(at.y)}" rx="${rx}" ry="${ry}"/>`
    } else spotAt.value = ''
  }
  const W = w + 2 * EDGE
  const H = h + 2 * EDGE
  const box = (r, pad) => {
    const l = Math.max(0, r.left - pad)
    const t = Math.max(0, r.top - pad)
    const width = Math.min(w, r.right + pad) - l
    const height = Math.min(h, r.bottom + pad) - t
    return width > 0 && height > 0 ? `<rect x="${l}" y="${t}" width="${width}" height="${height}"/>` : ''
  }
  if (privacy.mode === 'discreet_spot') {
    // The pane over the sensitive values only, the spot cut out of it.
    const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><mask id="m"><g transform="translate(${EDGE} ${EDGE})"><g fill="white">${sensitiveRects().map((r) => box(r, 2)).join('')}</g><g fill="black">${spot}</g></g></mask><rect width="${W}" height="${H}" mask="url(#m)"/></svg>`
    mask.value = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
    return
  }
  const holes = [...document.querySelectorAll(PUBLIC)].map(visible)
  const shapes = spot ? [spot] : []
  if (privacy.mode === 'full') for (const rect of [unitAt(pointer.x, pointer.y), focusUnit()]) if (rect) holes.push(rect)
  for (const r of holes) shapes.push(box(r, 4))
  // The pane reaches 48px past each edge (see .pv-pane): the windows moved in by as much.
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="${W}" height="${H}" viewBox="0 0 ${W} ${H}"><mask id="m"><rect width="${W}" height="${H}" fill="white"/><g fill="black" transform="translate(${EDGE} ${EDGE})">${shapes.join('')}</g></mask><rect width="${W}" height="${H}" mask="url(#m)"/></svg>`
  mask.value = `url("data:image/svg+xml,${encodeURIComponent(svg)}")`
}
const redraw = () => {
  if (!frame) frame = requestAnimationFrame(draw)
}
function lens(point) {
  if (!point) return
  if (Math.abs(point.clientX - pointer.x) + Math.abs(point.clientY - pointer.y) > 3) typing = false
  pointer.x = point.clientX
  pointer.y = point.clientY
  redraw()
}
function onPointer(event) {
  active()
  if (paned()) lens(event)
}
let tick = null
watch(() => [privacy.mode, privacy.covered], redraw)

onMounted(() => {
  window.addEventListener('keydown', onKey, true)
  window.addEventListener('pointermove', onPointer, { passive: true })
  window.addEventListener('wheel', onWheel, { passive: false })
  window.addEventListener('touchstart', onTouchStart, { passive: true })
  window.addEventListener('touchmove', (e) => paned() && lens(e.touches[0]), { passive: true })
  window.addEventListener('touchend', onTouchEnd, { passive: true })
  window.addEventListener('scroll', redraw, { passive: true, capture: true })
  window.addEventListener('resize', redraw)
  window.addEventListener('focusin', redraw)
  // The text cursor moves after the key: the spot follows it.
  window.addEventListener('input', redraw, true)
  document.addEventListener('selectionchange', redraw)
  window.addEventListener('blur', leave)
  document.addEventListener('visibilitychange', onVisibility)
  // The page changes under a still pointer (a list loads, a dialog opens).
  tick = setInterval(() => paned() && redraw(), 400)
  idleTimer = setInterval(() => {
    if (idleMinutes.value && !privacy.covered && Date.now() - lastActive > idleMinutes.value * 60000) privacy.cover()
  }, 15000)
})
onBeforeUnmount(() => {
  window.removeEventListener('keydown', onKey, true)
  window.removeEventListener('pointermove', onPointer)
  window.removeEventListener('wheel', onWheel)
  window.removeEventListener('touchstart', onTouchStart)
  window.removeEventListener('touchend', onTouchEnd)
  window.removeEventListener('scroll', redraw, { capture: true })
  window.removeEventListener('resize', redraw)
  window.removeEventListener('focusin', redraw)
  window.removeEventListener('input', redraw, true)
  document.removeEventListener('selectionchange', redraw)
  window.removeEventListener('blur', leave)
  document.removeEventListener('visibilitychange', onVisibility)
  clearInterval(tick)
  clearInterval(idleTimer)
})
</script>

<template>
  <div v-if="paned() && !privacy.covered" class="pv-pane" :style="{ maskImage: mask, WebkitMaskImage: mask }" :data-spot="spotAt || undefined" aria-hidden="true" data-testid="privacy-pane"></div>
  <div
    v-if="privacy.covered"
    class="pv-cover"
    role="button"
    tabindex="0"
    :aria-label="$t('privacy.uncover')"
    data-testid="privacy-cover"
    @click="privacy.uncover()"
    @touchend.prevent="privacy.uncover()"
  >
    <i class="fa-solid fa-eye-slash text-4xl text-faint" aria-hidden="true"></i>
    <p class="text-sm text-muted">{{ $t('privacy.covered') }}</p>
  </div>
</template>
